/**
 * Force Sync Service
 *
 * Thin client for the Salesforce Data Sync force-sync API. Kicks off a
 * sync-dispatcher Step Function execution in AWS. Returns 202 + executionArn;
 * the sync itself runs asynchronously.
 *
 * Docs: https://github.com/Regal-Credit-Management/salesforce-data-sync/blob/staging/docs/API/force-sync.md
 */

import { keycloakService } from './keycloakService';

export type SyncType = 'delta' | 'full';

export interface ForceSyncRequest {
  objectTypes: string[];
  syncType: SyncType;
  metadata?: Record<string, unknown>;
}

export interface ForceSyncSuccess {
  ok: true;
  executionArn: string;
  status: 'In Progress';
}

export interface ForceSyncFailure {
  ok: false;
  httpStatus: number;
  message: string;
}

export type ForceSyncResult = ForceSyncSuccess | ForceSyncFailure;

const FEATURE_ENABLED =
  process.env.REACT_APP_ENABLE_FORCE_SYNC === 'true';

const API_URL =
  process.env.REACT_APP_FORCE_SYNC_API_URL ||
  process.env.FORCE_SYNC_API_URL ||
  '';

export function isForceSyncEnabled(): boolean {
  return FEATURE_ENABLED && !!API_URL;
}

export function getForceSyncEndpoint(): string {
  return API_URL.replace(/\/$/, '') + '/force-sync';
}

export async function triggerForceSync(req: ForceSyncRequest): Promise<ForceSyncResult> {
  if (!isForceSyncEnabled()) {
    return {
      ok: false,
      httpStatus: 0,
      message:
        'Force sync is disabled. Set REACT_APP_ENABLE_FORCE_SYNC=true and REACT_APP_FORCE_SYNC_API_URL.',
    };
  }

  if (!Array.isArray(req.objectTypes) || req.objectTypes.length === 0) {
    return {
      ok: false,
      httpStatus: 0,
      message: 'objectTypes is required and must be a non-empty array of strings',
    };
  }
  if (req.syncType !== 'delta' && req.syncType !== 'full') {
    return {
      ok: false,
      httpStatus: 0,
      message: 'syncType is required and must be one of: delta, full',
    };
  }

  try {
    const token = keycloakService.getToken();
    const res = await fetch(getForceSyncEndpoint(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        objectTypes: req.objectTypes,
        syncType: req.syncType,
        metadata: req.metadata ?? {},
      }),
    });

    const raw = await res.text();
    let body: unknown = {};
    try {
      body = raw ? JSON.parse(raw) : {};
    } catch {
      body = { message: raw };
    }

    if (res.status === 202) {
      const { executionArn } = (body as { executionArn?: string });
      if (!executionArn) {
        return {
          ok: false,
          httpStatus: 202,
          message: 'Accepted but no executionArn in response.',
        };
      }
      return { ok: true, executionArn, status: 'In Progress' };
    }

    const message =
      (body as { message?: string }).message ||
      `Request failed with status ${res.status}`;
    return { ok: false, httpStatus: res.status, message };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Network error';
    return { ok: false, httpStatus: 0, message };
  }
}
